import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getCurrentBranchTime } from '../../utils/timezoneUtils';
import {
  Box,
  Typography,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  FormControlLabel,
  Switch,
  Snackbar,
  CircularProgress,
  Chip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import SaveIcon from '@mui/icons-material/Save';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import BarcodeScanner from '../common/BarcodeScanner';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import api, { apiWithContext } from '../../services/api';
import * as XLSX from 'xlsx';
import ProductAttributeSearch from '../invoices/ProductAttributeSearch';

// Import the Product type from types
import { Product as CatalogProduct, ProductVariant as CatalogProductVariant } from '../../types/product';

// Types
interface Warehouse {
  _id: string;
  name: string;
  location?: string;
}

interface ProductVariant {
  _id: string;
  sku: string;
  attributes: Record<string, string>;
  quantity: number;
}

interface Product {
  _id: string;
  name: string;
  sku: string;
  variants: ProductVariant[];
}

interface TransferItem {
  _id?: string;
  product: string;
  productName: string;
  variantId: string | null;
  variantAttributes: Record<string, string>;
  quantity: number;
  unit: string;
  unitName: string;
  sku?: string;
  barcode?: string;
  tempId?: string; // For frontend tracking only
  hasError?: boolean; // Flag to indicate validation errors
}

interface Unit {
  _id: string;
  name: string;
}

interface InventoryItem {
  _id: string;
  warehouse: string;
  product: string;
  productName: string;
  variantId: string | null;
  variantAttributes: Record<string, string>;
  quantity: number;
  unit: string;
  unitName: string;
  sku: string;
  barcode: string;
}

// Interface for grouped product search results
interface GroupedProduct {
  productId: string;
  productName: string;
  variants: InventoryItem[];
}

const WarehouseTransfer = () => {
  const navigate = useNavigate();
  const { transferId } = useParams<{ transferId?: string }>();
  const isEditMode = !!transferId;
  const { activeBranch } = useAuth();

  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [units, setUnits] = useState<Unit[]>([]);
  const [defaultUnitId, setDefaultUnitId] = useState<string>('');
  const [sourceWarehouse, setSourceWarehouse] = useState<string>('');
  const [destinationWarehouse, setDestinationWarehouse] = useState<string>('');
  const [transferStatus, setTransferStatus] = useState<'draft' | 'completed'>('draft');
  const [transferDate, setTransferDate] = useState<string>(() => {
    // استخدام توقيت الفرع من activeBranch أو الافتراضي Asia/Riyadh
    const branchTimezone = activeBranch?.timezone || 'Asia/Riyadh';
    const branchTime = getCurrentBranchTime(branchTimezone);
    return branchTime.format('YYYY-MM-DD');
  });
  const [notes, setNotes] = useState<string>('');
  const [items, setItems] = useState<TransferItem[]>([]);
  
  // Product search
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [searchResults, setSearchResults] = useState<InventoryItem[]>([]);
  const [groupedResults, setGroupedResults] = useState<GroupedProduct[]>([]);
  const [isSearchDialogOpen, setIsSearchDialogOpen] = useState<boolean>(false);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [filterTerm, setFilterTerm] = useState<string>('');
  const [expandedProducts, setExpandedProducts] = useState<Set<string>>(new Set());
  
  // Product Attribute Search state
  const [isProductAttributeSearchOpen, setIsProductAttributeSearchOpen] = useState<boolean>(false);

  // Warehouse selection dialogs
  const [isSourceWarehouseDialogOpen, setIsSourceWarehouseDialogOpen] = useState<boolean>(false);
  const [isDestWarehouseDialogOpen, setIsDestWarehouseDialogOpen] = useState<boolean>(false);
  
  // Barcode scanning
  const [isScannerOpen, setIsScannerOpen] = useState<boolean>(false);
  
  // Excel import related states
  const [isExcelUploadOpen, setIsExcelUploadOpen] = useState<boolean>(false);
  const [excelFile, setExcelFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  
  // Alerts and loading states
  const [alert, setAlert] = useState<{type: 'success' | 'error' | 'warning', message: string} | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  
  // Group search results by product
  const groupSearchResults = useCallback((results: InventoryItem[]) => {
    const groupedMap = new Map<string, GroupedProduct>();
    
    results.forEach(item => {
      if (!groupedMap.has(item.product)) {
        groupedMap.set(item.product, {
          productId: item.product,
          productName: item.productName,
          variants: []
        });
      }
      
      const group = groupedMap.get(item.product)!;
      group.variants.push(item);
    });
    
    return Array.from(groupedMap.values());
  }, []);
  
  // Set grouped results whenever search results change
  useEffect(() => {
    const grouped = groupSearchResults(searchResults);
    setGroupedResults(grouped);
  }, [searchResults, groupSearchResults]);
  
  // Filtered search results (raw)
  const filteredSearchResults = useMemo(() => {
    if (!filterTerm) return searchResults;
    
    const lowercasedFilter = filterTerm.toLowerCase();
    return searchResults.filter(item =>
      item.productName.toLowerCase().includes(lowercasedFilter) ||
      (item.sku && item.sku.toLowerCase().includes(lowercasedFilter)) ||
      (item.barcode && item.barcode.toLowerCase().includes(lowercasedFilter)) ||
      Object.entries(item.variantAttributes || {}).some(
        ([key, value]) => value.toLowerCase().includes(lowercasedFilter)
      )
    );
  }, [searchResults, filterTerm]);
  
  // Filtered grouped search results
  const filteredGroupedResults = useMemo(() => {
    if (!filterTerm) return groupedResults;
    
    const lowercasedFilter = filterTerm.toLowerCase();
    
    return groupedResults
      .map(group => {
        const filteredVariants = group.variants.filter(item =>
          item.productName.toLowerCase().includes(lowercasedFilter) ||
          (item.sku && item.sku.toLowerCase().includes(lowercasedFilter)) ||
          (item.barcode && item.barcode.toLowerCase().includes(lowercasedFilter)) ||
          Object.entries(item.variantAttributes || {}).some(
            ([key, value]) => value.toLowerCase().includes(lowercasedFilter)
          )
        );
        
        if (filteredVariants.length === 0) return null;
        
        return {
          ...group,
          variants: filteredVariants
        };
      })
      .filter((group): group is GroupedProduct => group !== null);
  }, [groupedResults, filterTerm]);

  // Load warehouses, units and existing transfer (if in edit mode) on component mount
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setIsLoading(true);
        
        // Fetch warehouses
        const warehousesResponse = await apiWithContext.get(`warehouse-transfers/warehouses`, 'warehouse-transfer');
        if (warehousesResponse.data.success) {
          setWarehouses(warehousesResponse.data.warehouses);
        }
        
        // Fetch available units
        const unitsResponse = await apiWithContext.get(`units`, 'warehouse-transfer');
        if (unitsResponse.data.success) {
          setUnits(unitsResponse.data.units);
          
          // Set default unit ID (piece or first available unit)
          const pieceUnit = unitsResponse.data.units.find((u: Unit) => u.name === 'قطعة' || u.name === 'piece');
          if (pieceUnit) {
            setDefaultUnitId(pieceUnit._id);
          } else if (unitsResponse.data.units.length > 0) {
            setDefaultUnitId(unitsResponse.data.units[0]._id);
          }
        }
        
        // If in edit mode, fetch the transfer details
        if (isEditMode && transferId) {
          const transferResponse = await api.get(`warehouse-transfers/${transferId}`);
          if (transferResponse.data.success) {
            const transfer = transferResponse.data.transfer;
            
            // Set transfer details
            setSourceWarehouse(transfer.sourceWarehouse);
            setDestinationWarehouse(transfer.destinationWarehouse);
            setTransferStatus(transfer.status);
            setNotes(transfer.notes || '');
            
            // Format date
            if (transfer.transferDate) {
              const date = new Date(transfer.transferDate);
              setTransferDate(date.toISOString().split('T')[0]);
            }
            
            // Set items with full variant details
            setItems(transfer.items.map((item: any) => ({
              _id: item._id,
              product: item.product,
              productName: item.productName,
              variantId: item.variantId,
              variantAttributes: item.variantAttributes || {},
              quantity: item.quantity,
              unit: item.unit,
              unitName: item.unitName,
              sku: item.sku,
              barcode: item.barcode,
              tempId: item._id || Math.random().toString(36).substring(2, 9)
            })));
          }
        }
      } catch (error) {
        setAlert({
          type: 'error',
          message: isEditMode ?
            'فشل في تحميل بيانات التحويل' :
            'فشل في تحميل المستودعات أو الوحدات'
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitialData();
  }, [isEditMode, transferId]);

  // Handle barcode scan
  const handleBarcodeScan = useCallback(async (barcode: string) => {
    if (!sourceWarehouse) {
      setAlert({
        type: 'error',
        message: 'الرجاء اختيار المستودع المصدر أولاً'
      });
      return;
    }

    try {
      setIsLoading(true);
      // Use the authenticated API service instead of direct axios call
      // تحسين استعلام البحث ليشمل معلمات barcode و additionalCodes لتفعيل البحث الدقيق
      const response = await apiWithContext.get(`inventory/search?term=${encodeURIComponent(barcode)}&barcode=${encodeURIComponent(barcode)}&additionalCodes=true&warehouse=${encodeURIComponent(sourceWarehouse)}&includeDetails=true&includeVariants=true`, 'warehouse-transfer');
      console.log(`البحث عن الباركود: ${barcode} في المستودع: ${sourceWarehouse} (مع الباركودات الإضافية)`);
      
      if (response.data.success && response.data.items && response.data.items.length > 0) {
        // تحسين فلترة النتائج للعثور على المنتج الذي يطابق الباركود بغض النظر عن حالة الأحرف
        const normalizedBarcode = barcode.toString().toLowerCase().trim();
        console.log(`البحث عن الباركود: ${normalizedBarcode} في ${response.data.items.length} منتج`);
        
        const matchingItems = response.data.items.filter((item: any) => {
          // البحث في الباركود الرئيسي
          if (item.barcode) {
            const itemBarcode = item.barcode.toString().toLowerCase().trim();
            if (itemBarcode === normalizedBarcode) {
              return true;
            }
          }
          // البحث في الباركودات الإضافية (إذا كانت موجودة)
          if (item.additionalCodes && Array.isArray(item.additionalCodes)) {
            const foundMatch = item.additionalCodes.some((additionalCode: string) => {
              const normalizedAdditionalCode = additionalCode.toString().toLowerCase().trim();
              const isMatch = normalizedAdditionalCode === normalizedBarcode;
              if (isMatch) {
                console.log(`تطابق باركود إضافي: ${additionalCode} للمنتج ${item.productName || item.name}`);
              }
              return isMatch;
            });
            if (foundMatch) return true;
          }
          
          
          return false;
        });
        
        console.log(`تم العثور على ${matchingItems.length} منتج مطابق`);
        const inventoryItem = matchingItems.length > 0 ? matchingItems[0] : response.data.items[0];
        
        // Check if item already exists in the list
        const existingItemIndex = items.findIndex(
          item => item.product === inventoryItem.product &&
                 item.variantId === inventoryItem.variantId
        );
        
        if (existingItemIndex >= 0) {
          // Update existing item quantity
          const updatedItems = [...items];
          updatedItems[existingItemIndex].quantity += 1;
          setItems(updatedItems);
        } else {
          // Add new item with validated unit ID
          const { unitId, unitName } = getValidUnitId(inventoryItem.unit, inventoryItem.unitName);
          
          const newItem: TransferItem = {
            product: inventoryItem.product,
            productName: inventoryItem.productName,
            variantId: inventoryItem.variantId,
            variantAttributes: inventoryItem.variantAttributes || {},
            quantity: 1,
            unit: unitId,
            unitName: unitName,
            sku: inventoryItem.sku,
            barcode: inventoryItem.barcode,
            tempId: Math.random().toString(36).substring(2, 9)
          };
          setItems(prevItems => [...prevItems, newItem]);
        }
        
        setAlert({
          type: 'success',
          message: 'تمت إضافة المنتج بنجاح'
        });
      } else {
        // المنتج غير موجود في المستودع المصدر، نحاول البحث في مستودعات أخرى ثم في قائمة المنتجات الكاملة
        try {
          // أولاً: البحث في جميع المستودعات (بدون تحديد المستودع في الطلب)
          console.log(`المنتج غير موجود في المستودع المصدر. البحث عن الباركود ${barcode} في جميع المستودعات...`);
          // إضافة معلمات barcode و additionalCodes للبحث الدقيق في الباركودات الإضافية
          const allWarehousesResponse = await apiWithContext.get(`inventory/search?term=${encodeURIComponent(barcode)}&barcode=${encodeURIComponent(barcode)}&additionalCodes=true&warehouse=${'all'}&includeDetails=true&includeVariants=true`, 'warehouse-transfer');
          
          if (allWarehousesResponse.data.success && allWarehousesResponse.data.items && allWarehousesResponse.data.items.length > 0) {
            // فلترة النتائج للعثور على المنتج الذي يطابق الباركود بغض النظر عن حالة الأحرف
            const normalizedBarcode = barcode.toString().toLowerCase().trim();
            const matchingItems = allWarehousesResponse.data.items.filter((item: any) => {
              // البحث في الباركود الرئيسي
              if (item.barcode) {
                const itemBarcode = item.barcode.toString().toLowerCase().trim();
                if (itemBarcode === normalizedBarcode) {
                  return true;
                }
              }
              
              // البحث في الباركودات الإضافية (إذا كانت موجودة)
              if (item.additionalCodes && Array.isArray(item.additionalCodes)) {
                const foundMatch = item.additionalCodes.some((additionalCode: string) => {
                  const normalizedAdditionalCode = additionalCode.toString().toLowerCase().trim();
                  const isMatch = normalizedAdditionalCode === normalizedBarcode;
                  if (isMatch) {
                    console.log(`تطابق باركود إضافي (في جميع المستودعات): ${additionalCode} للمنتج ${item.productName || item.name}`);
                  }
                  return isMatch;
                });
                if (foundMatch) return true;
              }
              
              return false;
            });
            
            if (matchingItems.length > 0) {
              const inventoryItem = matchingItems[0];
              console.log(`تم العثور على المنتج بالباركود ${barcode} في مستودع آخر: ${inventoryItem.warehouseName || 'غير معروف'}`);
              
              // إضافة المنتج من المستودع الآخر، لكن تنبيه المستخدم
              addProductFromSearch(inventoryItem, 1);
              setAlert({
                type: 'warning',
                message: `تم العثور على المنتج في مستودع ${inventoryItem.warehouseName || 'آخر'} وليس في المستودع المصدر المحدد`
              });
              return;
            }
          }
          
          // ثانياً: البحث في قائمة المنتجات الكاملة باستخدام endpoint الباركود المخصص
          const catalogResponse = await apiWithContext.post(`/products/search-by-barcode`, { barcode }, 'warehouse-transfer');
          
          if (catalogResponse.data.success && catalogResponse.data.product) {
            const foundProduct = catalogResponse.data.product;
            const foundVariant = catalogResponse.data.variant;
            
            if (foundProduct && foundVariant) {
              console.log(`تم العثور على المنتج بالباركود ${barcode} في قائمة المنتجات: ${foundProduct.name}`);
              // إضافة المنتج تلقائيًا
              handleProductAttributeSelect(foundProduct, foundVariant);
              setAlert({
                type: 'success',
                message: 'تم إضافة المنتج من قائمة المنتجات (غير موجود في المستودع المصدر)'
              });
              return;
            }
          }
          
          // تحسين الرسالة لتكون أكثر وضوحاً عندما لا يتم العثور على المنتج في كلا المكانين
          console.log(`الباركود ${barcode} غير موجود في المستودع ${sourceWarehouse} ولا في قائمة المنتجات الكاملة`);
          
          setAlert({
            type: 'warning',
            message: `باركود ${barcode} غير مسجل في النظام. يمكنك إضافة المنتج يدوياً من قائمة المنتجات.`
          });
          setIsProductAttributeSearchOpen(true);
        } catch (error) {
          console.error("Error searching catalog:", error);
          setAlert({
            type: 'warning',
            message: `حدث خطأ أثناء البحث عن الباركود ${barcode}. يمكنك البحث يدوياً عن المنتج.`
          });
          setIsProductAttributeSearchOpen(true);
        }
      }
    } catch (error) {
      setAlert({
        type: 'error',
        message: 'حدث خطأ أثناء البحث عن الباركود'
      });
    } finally {
      setIsLoading(false);
      setIsScannerOpen(false);
    }
  }, [sourceWarehouse, items]);

  // Toggle product expansion in search results
  const toggleProductExpansion = (productId: string) => {
    setExpandedProducts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) {
        newSet.delete(productId);
      } else {
        newSet.add(productId);
      }
      return newSet;
    });
  };

  // Handle product search in warehouse inventory
  const handleSearch = async () => {
    if (!sourceWarehouse || !searchTerm) {
      setAlert({
        type: 'error',
        message: 'الرجاء اختيار المستودع المصدر وإدخال مصطلح البحث'
      });
      return;
    }
  
    try {
      setIsSearching(true);
      // Using detailed search to get all product variants
      const response = await apiWithContext.get(`inventory/search?term=${encodeURIComponent(searchTerm)}&warehouse=${encodeURIComponent(sourceWarehouse)}&includeDetails=true&includeVariants=true`, 'warehouse-transfer');
      
      if (response.data.success && response.data.items.length > 0) {
        setSearchResults(response.data.items);
        // Auto-expand products with multiple variants
        const grouped = groupSearchResults(response.data.items);
        const autoExpand = new Set<string>();
        grouped.forEach(group => {
          if (group.variants.length > 1) {
            autoExpand.add(group.productId);
          }
        });
        setExpandedProducts(autoExpand);
        setIsSearchDialogOpen(true);
      } else {
        setAlert({
          type: 'error',
          message: 'لم يتم العثور على نتائج'
        });
      }
    } catch (error) {
      setAlert({
        type: 'error',
        message: 'حدث خطأ أثناء البحث'
      });
    } finally {
      setIsSearching(false);
    }
  };

  // Handle product selection from ProductAttributeSearch
  const handleProductAttributeSelect = (product: CatalogProduct, variant: CatalogProductVariant) => {
    const defaultBarcode = variant.barcodes.find(b => b.isDefault) || variant.barcodes[0];
    
    // Validate MongoDB ObjectId pattern for units
    const isValidObjectId = (id: string) => /^[0-9a-fA-F]{24}$/.test(id);
    
    // Get unit information
    let unitId = defaultBarcode?.unit || variant.unit || defaultUnitId || '';
    let unitName = defaultBarcode?.unit || variant.unit || (units.length > 0 ? units[0].name : 'قطعة');
    
    // Find the unit in our units array to get its name
    if (unitId && isValidObjectId(unitId)) {
      const unit = units.find(u => u._id === unitId);
      if (unit) {
        unitName = unit.name;
      } else {
        // The unit ID exists but doesn't match any unit in the array
        // Try to find a valid unit instead
        unitId = ''; // Reset to trigger fallback logic
      }
    }
    
    // Fallback if unitId is not set or invalid
    if (!unitId || !isValidObjectId(unitId)) {
      if (units.length > 0) {
        // Find the first valid unit in the array
        const validUnit = units.find(u => isValidObjectId(u._id));
        if (validUnit) {
          unitId = validUnit._id;
          unitName = validUnit.name;
        } else {
          // Error: No valid unit found
          setAlert({
            type: 'error',
            message: 'لا توجد وحدة صالحة متاحة. الرجاء التحقق من إعدادات الوحدات.'
          });
          return; // Don't proceed with adding the item
        }
      } else {
        // Error: No units available
        setAlert({
          type: 'error',
          message: 'لم يتم العثور على وحدات. الرجاء إضافة وحدات قبل إضافة منتجات.'
        });
        return; // Don't proceed with adding the item
      }
    }

    const newItem: TransferItem = {
      product: product._id || '',
      productName: product.name || '',
      variantId: variant._id || null,
      variantAttributes: variant.attributes || {},
      quantity: 1,
      unit: unitId,
      unitName: unitName,
      sku: variant.sku || '',
      barcode: defaultBarcode?.code || '',
      tempId: Date.now().toString() + Math.random().toString()
    };

    const existingItemIndex = items.findIndex(
      item => item.product === newItem.product && item.variantId === newItem.variantId
    );

    if (existingItemIndex >= 0) {
      const updatedItems = [...items];
      updatedItems[existingItemIndex].quantity += 1;
      setItems(updatedItems);
    } else {
      setItems([...items, newItem]);
    }

    setIsProductAttributeSearchOpen(false);
    setAlert({
      type: 'success',
      message: 'تمت إضافة المنتج بنجاح'
    });
  };

  // Handle multiple product selection from ProductAttributeSearch
  const handleMultiProductAttributeSelect = (selectedItems: { product: CatalogProduct; variant: CatalogProductVariant }[]) => {
    const newItems: TransferItem[] = [...items];

    selectedItems.forEach(({ product, variant }) => {
      const defaultBarcode = variant.barcodes.find(b => b.isDefault) || variant.barcodes[0];
      
      // Validate MongoDB ObjectId pattern for units
      const isValidObjectId = (id: string) => /^[0-9a-fA-F]{24}$/.test(id);
      
      // Get unit information
      let unitId = defaultBarcode?.unit || variant.unit || defaultUnitId || '';
      let unitName = defaultBarcode?.unit || variant.unit || (units.length > 0 ? units[0].name : 'قطعة');
      
      // Find the unit in our units array to get its name
      if (unitId && isValidObjectId(unitId)) {
        const unit = units.find(u => u._id === unitId);
        if (unit) {
          unitName = unit.name;
        } else {
          // The unit ID exists but doesn't match any unit in the array
          // Try to find a valid unit instead
          unitId = ''; // Reset to trigger fallback logic
        }
      }
      
      // Fallback if unitId is not set or invalid
      if (!unitId || !isValidObjectId(unitId)) {
        if (units.length > 0) {
          // Find the first valid unit in the array
          const validUnit = units.find(u => isValidObjectId(u._id));
          if (validUnit) {
            unitId = validUnit._id;
            unitName = validUnit.name;
          } else {
            // Error: No valid unit found - skip this item
            console.error('لا توجد وحدة صالحة متاحة للمنتج:', product.name);
            return; // Skip this item
          }
        } else {
          // Error: No units available - skip this item
          console.error('لم يتم العثور على وحدات للمنتج:', product.name);
          return; // Skip this item
        }
      }

      const newItem: TransferItem = {
        product: product._id || '',
        productName: product.name || '',
        variantId: variant._id || null,
        variantAttributes: variant.attributes || {},
        quantity: 1,
        unit: unitId,
        unitName: unitName,
        sku: variant.sku || '',
        barcode: defaultBarcode?.code || '',
        tempId: Date.now().toString() + Math.random().toString()
      };

      const existingItemIndex = newItems.findIndex(
        item => item.product === newItem.product && item.variantId === newItem.variantId
      );

      if (existingItemIndex >= 0) {
        newItems[existingItemIndex].quantity += 1;
      } else {
        newItems.push(newItem);
      }
    });

    setItems(newItems);
    setIsProductAttributeSearchOpen(false);
    setAlert({
      type: 'success',
      message: `تمت إضافة ${selectedItems.length} منتج بنجاح`
    });
  };

  // State for item quantity in dialog
  const [selectedQuantity, setSelectedQuantity] = useState<number>(1);
  const [selectedItem, setSelectedItem] = useState<InventoryItem | null>(null);

  // Add product from search results with specific quantity
  const addProductFromSearch = (inventoryItem: InventoryItem, quantity: number = 1) => {
    // تحقق من صحة البيانات قبل الإضافة
    if (!inventoryItem.product) {
      console.error("خطأ: معرف المنتج غير موجود", inventoryItem);
      setAlert({
        type: 'error',
        message: 'خطأ في بيانات المنتج: معرف المنتج غير موجود'
      });
      return;
    }

    // Check if item already exists in the list
    const existingItemIndex = items.findIndex(
      item => item.product === inventoryItem.product &&
             item.variantId === inventoryItem.variantId
    );
    
    if (existingItemIndex >= 0) {
      // Update existing item quantity
      const updatedItems = [...items];
      updatedItems[existingItemIndex].quantity += quantity;
      setItems(updatedItems);
    } else {
      // استخدام الوحدة الموجودة أو وحدة افتراضية
      const unitId = inventoryItem.unit || '';
      const unitName = inventoryItem.unitName || 'قطعة';
      
      // Add new item with all variant details and validated unit
      const newItem: TransferItem = {
        product: inventoryItem.product,
        productName: inventoryItem.productName,
        variantId: inventoryItem.variantId,
        variantAttributes: inventoryItem.variantAttributes || {},
        quantity: quantity,
        unit: unitId,
        unitName: unitName,
        sku: inventoryItem.sku,
        barcode: inventoryItem.barcode,
        tempId: Math.random().toString(36).substring(2, 9)
      };
      
      // تسجيل معلومات المنتج للتحقق
      console.log("إضافة منتج جديد:", {
        productName: newItem.productName,
        barcode: newItem.barcode,
        unit: newItem.unit,
        unitName: newItem.unitName
      });
      
      // تم إزالة التحقق من الكمية المتاحة هنا
      // يمكن إضافة المنتج بغض النظر عن توفره في المستودع المصدر
      
      setItems(prevItems => [...prevItems, newItem]);
    }
    
    setIsSearchDialogOpen(false);
    setSearchTerm('');
    setFilterTerm('');
    setSelectedQuantity(1);
    setSelectedItem(null);
    setSearchResults([]);
    setAlert({
      type: 'success',
      message: 'تمت إضافة المنتج بنجاح'
    });
  };

  // Show quantity dialog for a product
  const showQuantityDialog = (item: InventoryItem) => {
    setSelectedItem(item);
    setSelectedQuantity(1);
  };

  // Reset product selection
  const cancelProductSelection = () => {
    setSelectedItem(null);
    setSelectedQuantity(1);
  };

  // Update item quantity
  const updateItemQuantity = (index: number, quantity: number) => {
    if (quantity <= 0) return;
    
    const updatedItems = [...items];
    updatedItems[index].quantity = quantity;
    setItems(updatedItems);
  };

  // Remove item
  const removeItem = (index: number) => {
    const updatedItems = [...items];
    updatedItems.splice(index, 1);
    setItems(updatedItems);
  };

  // Save transfer
  const saveTransfer = async () => {
    if (!sourceWarehouse || !destinationWarehouse) {
      setAlert({
        type: 'error',
        message: 'الرجاء اختيار المستودع المصدر والمستودع الوجهة'
      });
      return;
    }

    if (sourceWarehouse === destinationWarehouse) {
      setAlert({
        type: 'error',
        message: 'لا يمكن التحويل إلى نفس المستودع'
      });
      return;
    }

    if (items.length === 0) {
      setAlert({
        type: 'error',
        message: 'الرجاء إضافة منتجات للتحويل'
      });
      return;
    }

    // Define type for item validation issues
    interface InvalidItem {
      index: number;
      name: string;
      issues: string[];
    }
    
    // Improved validation for items
    const invalidItems: InvalidItem[] = [];
    const itemsWithIssues: number[] = [];
    
    // Validate MongoDB ObjectId pattern
    const isValidObjectId = (id: string) => /^[0-9a-fA-F]{24}$/.test(id);
    
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const issues: string[] = [];
      
      // Check for valid product ID
      if (!item.product || !isValidObjectId(item.product)) {
        issues.push('معرف المنتج غير صالح');
      }
      
      // Check for valid unit ID
      if (!item.unit || !isValidObjectId(item.unit)) {
        issues.push('معرف الوحدة غير صالح');
      }
      
      // Check for positive quantity
      if (!item.quantity || item.quantity <= 0) {
        issues.push('الكمية يجب أن تكون أكبر من صفر');
      }
      
      if (issues.length > 0) {
        invalidItems.push({
          index: i + 1,
          name: item.productName,
          issues: issues
        });
        itemsWithIssues.push(i);
      }
    }
    
    if (invalidItems.length > 0) {
      // Create detailed error message
      const errorDetails = invalidItems.map(item =>
        `المنتج ${item.index}: ${item.name} - ${item.issues.join(', ')}`
      ).join('\n');
      
      console.error('Invalid items detected:', errorDetails);
      
      // Mark problem items in the UI
      const updatedItems = [...items];
      itemsWithIssues.forEach(index => {
        updatedItems[index] = {
          ...updatedItems[index],
          hasError: true
        };
      });
      setItems(updatedItems);
      
      // Show detailed error message to the user
      setAlert({
        type: 'error',
        message: `بعض المنتجات تحتوي على بيانات غير صالحة:\n${errorDetails}`
      });
      return;
    }

    try {
      setIsSaving(true);
      
      let currentTransferId = transferId;
      
      // تحويل التاريخ إلى تاريخ ووقت بتوقيت الفرع مع الحفاظ على offset
      const branchTimezone = activeBranch?.timezone || 'Asia/Riyadh';
      const moment = require('moment-timezone');
      const [year, month, day] = transferDate.split('-').map(Number);
      const now = moment.tz(branchTimezone);
      const transferDateTime = moment.tz({
        year,
        month: month - 1,
        day,
        hour: now.hour(),
        minute: now.minute(),
        second: now.second(),
        millisecond: now.millisecond()
      }, branchTimezone).format('YYYY-MM-DDTHH:mm:ss.SSSZ'); // صيغة ISO مع offset
      
      if (isEditMode && transferId) {
        // تحديث التحويل الموجود
        const updateResponse = await api.put(`warehouse-transfers/${transferId}`, {
          sourceWarehouseId: sourceWarehouse,
          destinationWarehouseId: destinationWarehouse,
          transferDate: transferDateTime,
          notes,
          items: items.map(item => ({
            productId: item.product,
            productName: item.productName,
            variantId: item.variantId,
            variantAttributes: item.variantAttributes,
            quantity: item.quantity,
            unitId: item.unit,
            unitName: item.unitName,
            sku: item.sku,
            barcode: item.barcode,
          }))
        });
        
        if (!updateResponse.data.success) {
          throw new Error(updateResponse.data.message);
        }
        
        // إذا كان التحويل في حالة مسودة وتم تغييره إلى مكتمل
        if (transferStatus === 'completed' && updateResponse.data.transfer.status !== 'completed') {
          const completeResponse = await api.post(`warehouse-transfers/${transferId}/complete`);
          
          if (!completeResponse.data.success) {
            throw new Error(completeResponse.data.message);
          }
        }
      } else {
        // إنشاء تحويل جديد
        const transferResponse = await api.post(`warehouse-transfers`, {
          sourceWarehouseId: sourceWarehouse,
          destinationWarehouseId: destinationWarehouse,
          transferDate: transferDateTime,
          notes
        });
        
        if (!transferResponse.data.success) {
          throw new Error(transferResponse.data.message);
        }
        
        currentTransferId = transferResponse.data.transfer._id;
        
        // إضافة العناصر إلى التحويل
        const itemsResponse = await api.post(`warehouse-transfers/${currentTransferId}/items`, {
          items: items.map(item => ({
            productId: item.product,
            productName: item.productName,
            variantId: item.variantId,
            variantAttributes: item.variantAttributes,
            quantity: item.quantity,
            unitId: item.unit,
            unitName: item.unitName,
            sku: item.sku,
            barcode: item.barcode,
          }))
        });
        
        if (!itemsResponse.data.success) {
          throw new Error(itemsResponse.data.message);
        }
        
        // إذا كان التحويل مكتمل، قم بإكمال العملية
        if (transferStatus === 'completed') {
          const completeResponse = await api.post(`warehouse-transfers/${currentTransferId}/complete`);
          
          if (!completeResponse.data.success) {
            throw new Error(completeResponse.data.message);
          }
        }
      }
      
      // تحديد رسالة النجاح بناءً على نوع العملية
      const successMessage = isEditMode
        ? (transferStatus === 'completed'
            ? 'تم تحديث وإكمال عملية التحويل بنجاح'
            : 'تم تحديث عملية التحويل بنجاح')
        : (transferStatus === 'completed'
            ? 'تم إكمال عملية التحويل بنجاح'
            : 'تم حفظ عملية التحويل كمسودة بنجاح');
      
      setAlert({
        type: 'success',
        message: successMessage
      });
      
      // في حالة التعديل، ننتقل إلى قائمة التحويلات
      if (isEditMode) {
        setTimeout(() => {
          navigate('/warehouses/transfer-list');
        }, 1500);
      } else {
        // في حالة الإنشاء، نعيد تعيين النموذج
        setSourceWarehouse('');
        setDestinationWarehouse('');
        setTransferStatus('draft');
        const branchTimezone = activeBranch?.timezone || 'Asia/Riyadh';
        const branchTime = getCurrentBranchTime(branchTimezone);
        setTransferDate(branchTime.format('YYYY-MM-DD'));
        setNotes('');
        setItems([]);
      }
      
    } catch (error: any) {
      setAlert({
        type: 'error',
        message: error.response?.data?.message || error.message || 'حدث خطأ أثناء حفظ عملية التحويل'
      });
    } finally {
      setIsSaving(false);
    }
  };
  
  // Function to generate and download Excel template
  const downloadExcelTemplate = () => {
    // Create a worksheet with headers
    const ws = XLSX.utils.aoa_to_sheet([
      ['الباركود', 'الكمية', 'وحدة القياس'],
      ['123456789', '10', 'قطعة']
    ]);
    
    // Add column widths
    const wscols = [
      { wch: 20 }, // Barcode
      { wch: 15 }, // Quantity
      { wch: 15 }  // Unit
    ];
    ws['!cols'] = wscols;
    
    // Create a workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'تحويل المخزون');
    
    // Generate file and trigger download
    XLSX.writeFile(wb, 'قالب_تحويل_المخزون.xlsx');
  };
  
  // Function to handle Excel file upload
  const handleExcelUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setExcelFile(file);
    }
  };
  
  // Function to process Excel data
  const processExcelData = async () => {
    if (!excelFile || !sourceWarehouse) {
      setAlert({
        type: 'error',
        message: 'الرجاء اختيار المستودع المصدر وملف Excel صالح'
      });
      return;
    }
    
    try {
      setIsUploading(true);
      
      // Read the Excel file
      const reader = new FileReader();
      reader.onload = async (e) => {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        
        // Get the first worksheet
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        
        // Convert to JSON
        const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];
        
        // Skip header row
        const dataRows = rows.slice(1);
        
        if (dataRows.length === 0) {
          setAlert({
            type: 'error',
            message: 'لم يتم العثور على بيانات في ملف Excel'
          });
          setIsUploading(false);
          return;
        }
        
        // تجهيز الصفوف الصالحة أولاً (باركود + كمية + وحدة)
        interface ParsedImportRow { rowNumber: number; barcode: string; quantity: number; unitName: string; }
        const parsedRows: ParsedImportRow[] = [];
        const newItems: TransferItem[] = [];
        const errors: string[] = [];
        const seenBarcodes = new Map<string, ParsedImportRow>();

        for (let i = 0; i < dataRows.length; i++) {
          const row = dataRows[i];
          if (!row || row.length < 1) continue; // تخطي الصفوف الفارغة

          const barcode = row[0] !== undefined && row[0] !== null ? row[0].toString().trim() : '';
          if (!barcode) {
            errors.push(`صف ${i + 2}: الباركود غير موجود`);
            continue;
          }

          const quantity = parseInt((row[1] ?? '0').toString(), 10);
          if (!quantity || isNaN(quantity) || quantity <= 0) {
            errors.push(`صف ${i + 2}: الكمية غير صالحة`);
            continue;
          }

          const unitName = row[2] !== undefined && row[2] !== null ? row[2].toString().trim() : '';

          const key = barcode.toLowerCase();
          const existing = seenBarcodes.get(key);
          if (existing) {
            // تجميع الكميات لنفس الباركود المكرر داخل الملف
            existing.quantity += quantity;
            continue;
          }

          const parsed: ParsedImportRow = { rowNumber: i + 2, barcode, quantity, unitName };
          seenBarcodes.set(key, parsed);
          parsedRows.push(parsed);
        }

        if (parsedRows.length > 0) {
          // بحث مجمّع واحد لكل الباركودات بدل طلب لكل صف (مقسّم إلى دفعات عند الحاجة)
          const CHUNK_SIZE = 500;
          const barcodeList = parsedRows.map(r => r.barcode);
          const barcodeResults = new Map<string, any>();

          for (let start = 0; start < barcodeList.length; start += CHUNK_SIZE) {
            const chunk = barcodeList.slice(start, start + CHUNK_SIZE);
            const searchResponse = await apiWithContext.post(
              `/inventory/search-barcodes`,
              { barcodes: chunk, warehouse: sourceWarehouse },
              'warehouse-transfer',
              { timeout: 120000 }
            );

            const chunkResults: any[] = searchResponse.data?.results || [];
            chunkResults.forEach((r: any) => {
              if (r && r.barcode) {
                barcodeResults.set(r.barcode.toString().trim().toLowerCase(), r);
              }
            });
          }

          for (const parsed of parsedRows) {
            const match = barcodeResults.get(parsed.barcode.trim().toLowerCase());

            if (!match || !match.found) {
              errors.push(`صف ${parsed.rowNumber}: باركود "${parsed.barcode}" غير مسجل في النظام`);
              continue;
            }

            // تحديد وحدة القياس: من الملف ثم من المخزون ثم الافتراضية
            let unitId = '';
            let resolvedUnitName = parsed.unitName;

            if (resolvedUnitName) {
              const unit = units.find(u => u.name.toLowerCase() === resolvedUnitName.toLowerCase());
              if (unit) {
                unitId = unit._id;
                resolvedUnitName = unit.name;
              }
            }

            if (!unitId && match.unit) {
              unitId = match.unit;
              if (match.unitName) resolvedUnitName = match.unitName;
            }

            if (!unitId) {
              const fallback = units.find(u => u._id === defaultUnitId) || units[0];
              if (fallback) {
                unitId = fallback._id;
                resolvedUnitName = fallback.name;
              }
            }

            if (!unitId) {
              errors.push(`صف ${parsed.rowNumber}: لا توجد وحدة قياس صالحة للمنتج "${match.productName || parsed.barcode}"`);
              continue;
            }

            const variantId = match.variantId || null;

            // منع تكرار نفس المنتج/المتغير داخل الاستيراد نفسه (تجميع الكمية)
            const existingIndex = newItems.findIndex(
              item => item.product === match.productId &&
                      (variantId ? item.variantId === variantId : !item.variantId)
            );

            if (existingIndex >= 0) {
              newItems[existingIndex].quantity += parsed.quantity;
            } else {
              newItems.push({
                product: match.productId,
                productName: match.productName || '',
                variantId: variantId,
                variantAttributes: match.variantAttributes || {},
                quantity: parsed.quantity,
                unit: unitId,
                unitName: resolvedUnitName || 'قطعة',
                sku: match.sku || '',
                barcode: match.mainBarcode || parsed.barcode,
                tempId: Math.random().toString(36).substring(2, 9)
              });
            }
          }
        }
        
        // Add items to the transfer
        if (newItems.length > 0) {
          // Merge with existing items
          const mergedItems = [...items];
          
          newItems.forEach(newItem => {
            const existingItemIndex = mergedItems.findIndex(
              item => item.product === newItem.product && item.variantId === newItem.variantId
            );
            
            if (existingItemIndex >= 0) {
              // Update existing item quantity
              mergedItems[existingItemIndex].quantity += newItem.quantity;
            } else {
              // Add new item
              mergedItems.push(newItem);
            }
          });
          
          setItems(mergedItems);
          
          setAlert({
            type: 'success',
            message: `تم استيراد ${newItems.length} منتج بنجاح${errors.length > 0 ? ' مع بعض الأخطاء' : ''}`
          });
        } else {
          setAlert({
            type: 'error',
            message: 'لم يتم استيراد أي منتجات'
          });
        }
        
        // Show errors if any
        if (errors.length > 0) {
          console.error('Excel import errors:', errors);
          setTimeout(() => {
            setAlert({
              type: 'error',
              message: `الأخطاء:\n${errors.join('\n')}`
            });
          }, 3000);
        }
        
        setIsExcelUploadOpen(false);
        setExcelFile(null);
        setIsUploading(false);
      };
      
      reader.readAsArrayBuffer(excelFile);
      
    } catch (error) {
      setAlert({
        type: 'error',
        message: 'حدث خطأ أثناء معالجة ملف Excel'
      });
      setIsUploading(false);
    }
  };

  // Utility function to validate and ensure a valid unit ID
  const getValidUnitId = (unitId: string, unitName: string = 'قطعة', unitsArray: Unit[] = units): { unitId: string, unitName: string } => {
    // Validate MongoDB ObjectId pattern
    const isValidObjectId = (id: string) => /^[0-9a-fA-F]{24}$/.test(id);
    
    // If the provided ID is valid and exists in our units array, use it
    if (unitId && isValidObjectId(unitId)) {
      const unit = unitsArray.find(u => u._id === unitId);
      if (unit) {
        return { unitId, unitName: unit.name };
      }
    }
    
    // Try to find a unit by name
    if (unitName) {
      const unitByName = unitsArray.find(u => u.name === unitName);
      if (unitByName && isValidObjectId(unitByName._id)) {
        return { unitId: unitByName._id, unitName: unitByName.name };
      }
    }
    
    // Try to use default unit
    if (defaultUnitId && isValidObjectId(defaultUnitId)) {
      const defaultUnit = unitsArray.find(u => u._id === defaultUnitId);
      if (defaultUnit) {
        return { unitId: defaultUnitId, unitName: defaultUnit.name };
      }
    }
    
    // Last resort: get the first valid unit from the units array
    if (unitsArray.length > 0) {
      const validUnit = unitsArray.find(u => isValidObjectId(u._id));
      if (validUnit) {
        return { unitId: validUnit._id, unitName: validUnit.name };
      }
    }
    
    // If we reach here, we couldn't find a valid unit
    return { unitId: '', unitName: 'قطعة' };
  };

  // Close alert
  const handleCloseAlert = () => {
    setAlert(null);
  };

  // Get warehouse name by ID
  const getWarehouseName = (id: string) => {
    const warehouse = warehouses.find(w => w._id === id);
    return warehouse ? warehouse.name : '';
  };

  // Format variant attributes for display
  const formatVariantAttributes = (attributes: Record<string, string>) => {
    return Object.entries(attributes)
      .map(([key, value]) => `${key}: ${value}`)
      .join(', ');
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
        تحويل بين المستودعات
      </Typography>
      
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3}>
          {/* Source Warehouse */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>من المستودع</Typography>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                minHeight: '100px',
                bgcolor: sourceWarehouse ? 'background.paper' : 'action.hover',
                '&:hover': {
                  bgcolor: 'action.hover',
                }
              }}
              onClick={() => setIsSourceWarehouseDialogOpen(true)}
            >
              {sourceWarehouse ? (
                <>
                  <Typography variant="h6">
                    {getWarehouseName(sourceWarehouse)}
                  </Typography>
                  {warehouses.find(w => w._id === sourceWarehouse)?.location && (
                    <Typography variant="body2" color="text.secondary">
                      {warehouses.find(w => w._id === sourceWarehouse)?.location}
                    </Typography>
                  )}
                </>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <Typography variant="body1" color="text.secondary">
                    اختر المستودع المصدر
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
          
          {/* Destination Warehouse */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>إلى المستودع</Typography>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                minHeight: '100px',
                bgcolor: destinationWarehouse ? 'background.paper' : 'action.hover',
                '&:hover': {
                  bgcolor: 'action.hover',
                }
              }}
              onClick={() => setIsDestWarehouseDialogOpen(true)}
            >
              {destinationWarehouse ? (
                <>
                  <Typography variant="h6">
                    {getWarehouseName(destinationWarehouse)}
                  </Typography>
                  {warehouses.find(w => w._id === destinationWarehouse)?.location && (
                    <Typography variant="body2" color="text.secondary">
                      {warehouses.find(w => w._id === destinationWarehouse)?.location}
                    </Typography>
                  )}
                </>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <Typography variant="body1" color="text.secondary">
                    اختر المستودع الوجهة
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
          
          {/* Transfer Status */}
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={transferStatus === 'completed'}
                  onChange={(e) => setTransferStatus(e.target.checked ? 'completed' : 'draft')}
                  disabled={isSaving}
                />
              }
              label={transferStatus === 'completed' ? 'تم التحويل' : 'مسودة'}
            />
            <Typography variant="caption" color="text.secondary" display="block">
              {transferStatus === 'completed' 
                ? 'سيتم نقل الكميات وتحديث المخزون فوراً' 
                : 'لن يتم تحديث المخزون حتى يتم تغيير الحالة إلى "تم التحويل"'}
            </Typography>
          </Grid>
          
          {/* Transfer Date */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>تاريخ التحويل</Typography>
            <TextField
              fullWidth
              type="date"
              value={transferDate}
              onChange={(e) => setTransferDate(e.target.value)}
              disabled={isSaving}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          
          {/* Notes */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="ملاحظات"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              multiline
              rows={1}
              disabled={isSaving}
            />
          </Grid>
        </Grid>
      </Paper>
      
      {/* Barcode Scanner and Search Buttons Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          {/* Barcode Scanner */}
          <Grid item xs={12} md={4}>
            {!sourceWarehouse ? (
              <Alert severity="info">الرجاء اختيار المستودع المصدر أولاً لاستخدام قارئ الباركود</Alert>
            ) : (
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>قارئ الباركود</Typography>
                <BarcodeScanner onScan={handleBarcodeScan} />
              </Box>
            )}
          </Grid>
          
          {/* Search and Excel Buttons */}
          <Grid item xs={12} md={5}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
              {/* Search Buttons */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<SearchIcon />}
                  onClick={() => {
                    if (sourceWarehouse) {
                      // Set a temporary search term if empty
                      if (!searchTerm) setSearchTerm('*');
                      handleSearch();
                    } else {
                      setAlert({
                        type: 'error',
                        message: 'الرجاء اختيار المستودع المصدر أولاً'
                      });
                    }
                  }}
                  disabled={isSaving}
                  fullWidth
                  sx={{ flex: 1 }}
                >
                  بحث في المخزون
                </Button>
                
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<SearchIcon />}
                  onClick={() => setIsProductAttributeSearchOpen(true)}
                  disabled={isSaving}
                  fullWidth
                  sx={{ flex: 1 }}
                >
                  قائمة المنتجات
                </Button>
              </Box>
              
              {/* Excel Import/Export Buttons */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  color="success"
                  startIcon={<FileDownloadIcon />}
                  onClick={downloadExcelTemplate}
                  disabled={isSaving}
                  fullWidth
                  sx={{ flex: 1 }}
                >
                  تنزيل قالب Excel
                </Button>
                
                <Button
                  variant="outlined"
                  color="info"
                  startIcon={<UploadFileIcon />}
                  onClick={() => setIsExcelUploadOpen(true)}
                  disabled={isSaving || !sourceWarehouse}
                  fullWidth
                  sx={{ flex: 1 }}
                >
                  استيراد من Excel
                </Button>
              </Box>
            </Box>
          </Grid>
          
          {/* Save Button */}
          <Grid item xs={12} md={3}>
            <Button
              variant="contained"
              color="primary"
              startIcon={transferStatus === 'completed' ? <CheckCircleIcon /> : <SaveIcon />}
              onClick={saveTransfer}
              disabled={isSaving || items.length === 0}
              fullWidth
              sx={{ height: '100%', minHeight: '110px' }}
            >
              {isSaving ? <CircularProgress size={24} /> :
                isEditMode
                  ? (transferStatus === 'completed' ? 'تحديث وإتمام التحويل' : 'تحديث التحويل')
                  : (transferStatus === 'completed' ? 'إتمام التحويل' : 'حفظ كمسودة')}
            </Button>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Items Table */}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        {/* Total Quantities Summary */}
        {items.length > 0 && (
          <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
            <Typography variant="h6" sx={{ textAlign: 'center', fontWeight: 'bold' }}>
              إجمالي الكميات: {items.reduce((total, item) => total + (item.quantity || 0), 0)} قطعة
            </Typography>
          </Box>
        )}
        <TableContainer sx={{ maxHeight: 440 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>المنتج</TableCell>
                <TableCell>الخصائص</TableCell>
                <TableCell>الكمية</TableCell>
                <TableCell>الوحدة</TableCell>
                <TableCell>SKU / الباركود</TableCell>
                <TableCell>إجراءات</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length > 0 ? (
                items.map((item, index) => (
                  <TableRow
                    key={item.tempId || item._id}
                    sx={{
                      bgcolor: item.hasError ? 'rgba(211, 47, 47, 0.1)' : 'inherit',
                      '&:hover': {
                        bgcolor: item.hasError ? 'rgba(211, 47, 47, 0.15)' : 'action.hover',
                      },
                      border: item.hasError ? '1px solid' : 'inherit',
                      borderColor: item.hasError ? 'error.main' : 'inherit'
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {item.hasError && (
                          <Chip
                            label="خطأ"
                            color="error"
                            size="small"
                            sx={{ mr: 1, mb: 1 }}
                          />
                        )}
                        {item.productName}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {item.variantId ? (
                        <Box sx={{ maxWidth: 220 }}>
                          {Object.entries(item.variantAttributes).map(([key, value]) => (
                            <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography variant="caption" sx={{ fontWeight: 'bold', mr: 1 }}>
                                {key}:
                              </Typography>
                              <Typography variant="caption">
                                {value}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">المنتج الأساسي</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        value={item.quantity}
                        onChange={(e) => updateItemQuantity(index, parseInt(e.target.value))}
                        InputProps={{ inputProps: { min: 1 } }}
                        size="small"
                        disabled={isSaving}
                        error={item.hasError}
                      />
                    </TableCell>
                    <TableCell>{item.unitName}</TableCell>
                    <TableCell>
                      {item.sku && (
                        <Typography variant="caption" display="block">
                          <strong>SKU:</strong> {item.sku}
                        </Typography>
                      )}
                      {item.barcode && (
                        <Typography variant="caption" display="block">
                          <strong>باركود:</strong> {item.barcode}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        color="error"
                        onClick={() => removeItem(index)}
                        disabled={isSaving}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    لا توجد منتجات مضافة بعد
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      
      {/* Removed Barcode Scanner Dialog - now integrated directly on page */}
      
      {/* Product Search Results Dialog */}
      <Dialog open={isSearchDialogOpen} onClose={() => setIsSearchDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">نتائج البحث عن المنتجات</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                placeholder="تصفية النتائج"
                size="small"
                sx={{ width: '220px' }}
                value={filterTerm}
                onChange={(e) => setFilterTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ color: 'action.active', mr: 1 }} />,
                }}
              />
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setFilterTerm('');
                  setExpandedProducts(new Set(groupedResults.map(g => g.productId)));
                }}
              >
                عرض الكل
              </Button>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {isSearching ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : filteredGroupedResults.length > 0 ? (
            <Box sx={{ maxHeight: '60vh', overflow: 'auto' }}>
              {filteredGroupedResults.map((group) => (
                <Paper
                  key={group.productId}
                  elevation={1}
                  sx={{
                    mb: 2,
                    borderRadius: 1,
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'divider'
                  }}
                >
                  {/* Product Header - Clickable to expand/collapse */}
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: 'action.hover',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.selected' }
                    }}
                    onClick={() => toggleProductExpansion(group.productId)}
                  >
                    <Box>
                      <Typography variant="h6">{group.productName}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {group.variants.length} {group.variants.length === 1 ? 'منتج' : 'منتجات'} |
                        {group.variants.some(v => v.quantity > 0) ?
                          ` متوفر: ${group.variants.reduce((sum, v) => sum + v.quantity, 0)} وحدة` :
                          ' غير متوفر في المخزون'}
                      </Typography>
                    </Box>
                    <Box>
                      {expandedProducts.has(group.productId) ? (
                        <Typography variant="body2" color="primary">إخفاء التفاصيل</Typography>
                      ) : (
                        <Typography variant="body2" color="primary">عرض التفاصيل</Typography>
                      )}
                    </Box>
                  </Box>
                  
                  {/* Expanded Variants Table */}
                  {expandedProducts.has(group.productId) && (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>الخصائص</TableCell>
                            <TableCell>SKU</TableCell>
                            <TableCell>الباركود</TableCell>
                            {/* Dynamically render attribute columns based on first variant */}
                            {group.variants.length > 0 && Object.keys(group.variants[0]?.variantAttributes || {}).map(attrKey => (
                              <TableCell key={`header-${attrKey}`}>{attrKey}</TableCell>
                            ))}
                            <TableCell>الكمية المتاحة</TableCell>
                            <TableCell>الوحدة</TableCell>
                            <TableCell>إجراءات</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {group.variants.map((variant) => (
                            <TableRow
                              key={`${variant.product}-${variant.variantId || 'base'}`}
                              hover
                              sx={{
                                bgcolor: 'inherit',
                                '&:hover': {
                                  bgcolor: 'action.hover',
                                }
                              }}
                            >
                              <TableCell>
                                {variant.variantId ?
                                  formatVariantAttributes(variant.variantAttributes) :
                                  'المنتج الأساسي'}
                              </TableCell>
                              <TableCell>{variant.sku || '-'}</TableCell>
                              <TableCell>{variant.barcode || '-'}</TableCell>
                              
                              {/* Dynamically render attribute values */}
                              {Object.keys(group.variants[0]?.variantAttributes || {}).map(attrKey => (
                                <TableCell key={`cell-${variant.product}-${variant.variantId || 'base'}-${attrKey}`}>
                                  {variant.variantAttributes?.[attrKey] || '-'}
                                </TableCell>
                              ))}
                              
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Typography
                                    sx={{
                                      color: 'text.primary',
                                      fontWeight: 'bold'
                                    }}
                                  >
                                    {variant.quantity}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>{variant.unitName}</TableCell>
                              <TableCell>
                                <Button
                                  variant="contained"
                                  size="small"
                                  startIcon={<AddIcon />}
                                  onClick={() => showQuantityDialog(variant)}
                                  color="primary"
                                >
                                  إضافة
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                  
                  {/* Quick Add Section (when collapsed) */}
                  {!expandedProducts.has(group.productId) && group.variants.some(v => v.quantity > 0) && (
                    <Box sx={{ p: 2 }}>
                      <Grid container spacing={2}>
                        {group.variants.map(variant => (
                          <Grid item xs={12} sm={6} md={4} key={`${variant.product}-${variant.variantId || 'base'}-btn`}>
                            <Paper
                              variant="outlined"
                              sx={{
                                p: 1.5,
                                borderColor: variant.quantity < 5 ? 'warning.main' : 'primary.main',
                                '&:hover': { bgcolor: 'action.hover' },
                                cursor: 'pointer'
                              }}
                              onClick={() => showQuantityDialog(variant)}
                            >
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                  {variant.variantId ? 'متغير المنتج' : 'المنتج الأساسي'}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    py: 0.5,
                                    px: 1,
                                    borderRadius: 1,
                                    bgcolor: 'primary.main',
                                    color: 'white',
                                    fontWeight: 'bold'
                                  }}
                                >
                                  {variant.quantity} {variant.unitName}
                                </Typography>
                              </Box>
                              
                              {variant.sku && (
                                <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                                  <strong>SKU:</strong> {variant.sku}
                                </Typography>
                              )}
                              
                              {Object.entries(variant.variantAttributes || {}).map(([key, value]) => (
                                <Typography key={key} variant="body2" sx={{ mb: 0.5 }}>
                                  <strong>{key}:</strong> {value}
                                </Typography>
                              ))}
                              
                              <Button
                                variant="contained"
                                size="small"
                                startIcon={<AddIcon />}
                                fullWidth
                                sx={{ mt: 1 }}
                                color={variant.quantity < 5 ? "warning" : "primary"}
                              >
                                إضافة للتحويل
                              </Button>
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}
                </Paper>
              ))}
            </Box>
          ) : (
            <Typography variant="body1" sx={{ p: 2 }}>
              لم يتم العثور على نتائج
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsSearchDialogOpen(false)}>إغلاق</Button>
        </DialogActions>
      </Dialog>
      
      {/* Source Warehouse Selection Dialog */}
      <Dialog
        open={isSourceWarehouseDialogOpen}
        onClose={() => setIsSourceWarehouseDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>اختر المستودع المصدر</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              {warehouses.map((warehouse) => (
                <Grid item xs={12} sm={6} md={4} key={warehouse._id}>
                  <Paper
                    elevation={sourceWarehouse === warehouse._id ? 3 : 1}
                    sx={{
                      p: 2,
                      borderColor: sourceWarehouse === warehouse._id ? 'primary.main' : 'divider',
                      borderWidth: sourceWarehouse === warehouse._id ? 2 : 1,
                      borderStyle: 'solid',
                      cursor: 'pointer',
                      bgcolor: sourceWarehouse === warehouse._id ? 'action.selected' : 'background.paper',
                      '&:hover': {
                        bgcolor: 'action.hover',
                      },
                      height: '100%'
                    }}
                    onClick={() => {
                      setSourceWarehouse(warehouse._id);
                      // Clear destination warehouse if same as source
                      if (destinationWarehouse === warehouse._id) {
                        setDestinationWarehouse('');
                      }
                      setIsSourceWarehouseDialogOpen(false);
                    }}
                  >
                    <Typography variant="h6" component="h3">{warehouse.name}</Typography>
                    {warehouse.location && (
                      <Typography variant="body2" color="text.secondary">{warehouse.location}</Typography>
                    )}
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsSourceWarehouseDialogOpen(false)}>إلغاء</Button>
        </DialogActions>
      </Dialog>

      {/* Destination Warehouse Selection Dialog */}
      <Dialog
        open={isDestWarehouseDialogOpen}
        onClose={() => setIsDestWarehouseDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>اختر المستودع الوجهة</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              {warehouses.map((warehouse) => (
                <Grid item xs={12} sm={6} md={4} key={warehouse._id}>
                  <Paper
                    elevation={destinationWarehouse === warehouse._id ? 3 : 1}
                    sx={{
                      p: 2,
                      borderColor: destinationWarehouse === warehouse._id ? 'primary.main' : 'divider',
                      borderWidth: destinationWarehouse === warehouse._id ? 2 : 1,
                      borderStyle: 'solid',
                      cursor: warehouse._id === sourceWarehouse ? 'not-allowed' : 'pointer',
                      bgcolor: warehouse._id === sourceWarehouse
                        ? 'action.disabledBackground'
                        : destinationWarehouse === warehouse._id
                          ? 'action.selected'
                          : 'background.paper',
                      opacity: warehouse._id === sourceWarehouse ? 0.6 : 1,
                      '&:hover': {
                        bgcolor: warehouse._id === sourceWarehouse ? 'action.disabledBackground' : 'action.hover',
                      },
                      height: '100%'
                    }}
                    onClick={() => {
                      if (warehouse._id !== sourceWarehouse) {
                        setDestinationWarehouse(warehouse._id);
                        setIsDestWarehouseDialogOpen(false);
                      }
                    }}
                  >
                    <Typography variant="h6" component="h3">{warehouse.name}</Typography>
                    {warehouse.location && (
                      <Typography variant="body2" color="text.secondary">{warehouse.location}</Typography>
                    )}
                    {warehouse._id === sourceWarehouse && (
                      <Typography variant="caption" color="error">
                        لا يمكن التحويل إلى نفس المستودع المصدر
                      </Typography>
                    )}
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDestWarehouseDialogOpen(false)}>إلغاء</Button>
        </DialogActions>
      </Dialog>
      
      {/* Quantity Selection Dialog */}
      <Dialog open={!!selectedItem} onClose={cancelProductSelection}>
        <DialogTitle>تحديد الكمية</DialogTitle>
        <DialogContent>
          {selectedItem && (
            <Box sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                {selectedItem.productName}
              </Typography>
              
              {selectedItem.variantId && (
                <Box sx={{ my: 2, p: 2, bgcolor: 'rgba(0, 0, 0, 0.03)', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    متغيرات المنتج:
                  </Typography>
                  {Object.entries(selectedItem.variantAttributes || {}).map(([key, value]) => (
                    <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                        {key}:
                      </Typography>
                      <Typography variant="body2">
                        {value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
              
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, mb: 1 }}>
                <Typography variant="body1" sx={{ mr: 2 }}>
                  الكمية:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                  {selectedItem.unitName}
                </Typography>
              </Box>
              
              <TextField
                fullWidth
                label="الكمية المطلوبة"
                variant="outlined"
                type="number"
                value={selectedQuantity}
                onChange={(e) => {
                  const value = parseInt(e.target.value);
                  if (value > 0) {
                    setSelectedQuantity(value);
                  }
                }}
                InputProps={{
                  inputProps: {
                    min: 1,
                    // تم إزالة القيود على الحد الأقصى للكمية للسماح بإضافة أي كمية
                  },
                  endAdornment: <Typography variant="body2">{selectedItem.unitName}</Typography>
                }}
                // Removed quantity validation
                sx={{ mt: 2 }}
              />
              
              {selectedItem.sku && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  SKU: {selectedItem.sku}
                </Typography>
              )}
              
              {selectedItem.barcode && (
                <Typography variant="body2" color="text.secondary">
                  الباركود: {selectedItem.barcode}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelProductSelection}>إلغاء</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              if (selectedItem) {
                addProductFromSearch(selectedItem, selectedQuantity);
              }
            }}
            disabled={!selectedItem || selectedQuantity <= 0} // Only check if quantity is positive
          >
            إضافة
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Product Attribute Search Dialog */}
      <ProductAttributeSearch
        open={isProductAttributeSearchOpen}
        onClose={() => setIsProductAttributeSearchOpen(false)}
        onSelectVariant={handleProductAttributeSelect}
        onSelectVariants={handleMultiProductAttributeSelect}
        multiSelect
      />

      {/* Excel Upload Dialog */}
      <Dialog
        open={isExcelUploadOpen}
        onClose={() => !isUploading && setIsExcelUploadOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>استيراد المنتجات من ملف Excel</DialogTitle>
        <DialogContent>
          <Box sx={{ p: 2 }}>
            <Typography variant="body1" paragraph>
              قم بتحميل ملف Excel يحتوي على بيانات المنتجات المراد إضافتها إلى التحويل.
            </Typography>
            
            <Typography variant="body2" color="text.secondary" paragraph>
              يجب أن يحتوي الملف على الأعمدة التالية: الباركود، الكمية، وحدة القياس
            </Typography>
            
            {!sourceWarehouse ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                الرجاء اختيار المستودع المصدر أولاً قبل استيراد المنتجات
              </Alert>
            ) : (
              <Typography variant="body2" sx={{ mb: 2 }}>
                سيتم البحث عن المنتجات في المستودع: <strong>{getWarehouseName(sourceWarehouse)}</strong>
              </Typography>
            )}
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <Button
                variant="outlined"
                component="label"
                startIcon={<FileDownloadIcon />}
                onClick={downloadExcelTemplate}
              >
                تنزيل قالب Excel
              </Button>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button
                  variant="contained"
                  component="label"
                  disabled={isUploading}
                >
                  اختيار ملف Excel
                  <input
                    type="file"
                    hidden
                    accept=".xlsx,.xls"
                    onChange={handleExcelUpload}
                  />
                </Button>
                {excelFile && (
                  <Typography variant="body2">
                    {excelFile.name}
                  </Typography>
                )}
              </Box>
            </Box>
            
            {isUploading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <CircularProgress />
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIsExcelUploadOpen(false)}
            disabled={isUploading}
          >
            إلغاء
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={processExcelData}
            disabled={!excelFile || isUploading || !sourceWarehouse}
          >
            استيراد
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Alert Snackbar */}
      <Snackbar
        open={!!alert}
        autoHideDuration={6000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseAlert} severity={alert?.type || 'info'} sx={{ width: '100%', ...(alert?.type === 'warning' && { bgcolor: 'warning.light' }) }}>
          <div style={{ whiteSpace: 'pre-line' }}>
            {alert?.message || ''}
          </div>
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WarehouseTransfer;